import React from "react";
import type { KanbanColumnData } from "./types";
import { KanbanCard } from "./KanbanCard";
import { useDroppable } from "@dnd-kit/core"; // For identifying the column as a drop zone
import { motion, AnimatePresence } from "framer-motion"; // Added import
import { hexToRgba } from "@/utils/colorUtils";
// We won't use SortableContext here directly; it will be in KanbanBoard per column

interface KanbanColumnProps {
  column: KanbanColumnData;
  isDraggingActive?: boolean; // Added prop
  activeCardId?: string | number | null; // Added prop
  variant?: 'default' | 'compact';
  // Cards are passed directly for rendering, dnd-kit context will handle sortability
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  isDraggingActive,
  activeCardId,
  variant = 'default',
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: "column", column }, // Optional: useful data for drag events
  });

  // Default to a light slate gray if no color is provided
  const columnColor = column.color || "#94a3b8"; // slate-500

  // Function to generate RGBA from hex and alpha
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const columnBaseStyles: React.CSSProperties = {
    backgroundColor: hexToRgba(columnColor, 0.1), // 5% opacity for base
  };

  const columnDragOverStyles: React.CSSProperties = {
    backgroundColor: hexToRgba(columnColor, 0.2), // 20% opacity when dragging over
  };

  const columnClasses =
    "flex flex-col min-w-[280px] rounded-2xl";

  const columnStyles = {
    ...(isOver ? columnDragOverStyles : columnBaseStyles),
    flex: 1,
  };
  // Removed dark:bg-slate-800, will be handled by dynamic style or could be added back if needed

  return (
    <div ref={setNodeRef} className={columnClasses} style={columnStyles}>
      <div
        className="p-3 pt-2 pb-0 sticky top-0 z-10 rounded-t-lg"
        // Slightly more opaque header
      >
        <div className="font-mono text-xs text-slate-700 dark:text-slate-200 uppercase tracking-wider flex justify-between items-center">
          <div
            className="flex items-center gap-2 rounded-full p-1 px-2 inline-flex"
            style={{ backgroundColor: hexToRgba(columnColor, 0.4) }}
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: hexToRgba(columnColor, 1) }}
            ></div>
            <div className="relative top-[1px]">{column.title}</div>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {column.cards.length}
          </div>
        </div>
      </div>

      <div
        className={`flex-grow p-3 space-y-3 min-h-[120px] transition-colors duration-200 ease-in-out overflow-y-auto rounded-b-lg`}
        // The main background is now handled by the parent div's style prop
        // The isOver effect is also handled by the parent div's style prop
        // Removed: ${ isOver ? "bg-sky-100 dark:bg-sky-900" : "bg-slate-50 dark:bg-slate-800/50" }
      >
        <AnimatePresence initial={false}>
          {column.cards.map((card) => {
            if (activeCardId && card.id === activeCardId) {
              // If this card is being actively dragged (and represented by DragOverlay),
              // render it without Framer Motion wrapper to avoid conflicts.
              // dnd-kit's useSortable hook in KanbanCard will still apply styles to make it a placeholder.
              // Ensure it still has a key for React reconciliation.
              return (
                <KanbanCard
                  key={card.id}
                  card={card}
                  columnId={column.id}
                  columnColor={columnColor}
                  variant={variant}
                />
              );
            }
            return (
              <motion.div
                key={card.id}
                layout={isDraggingActive ? false : true}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0,
                  marginBottom: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  y: -10,
                }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                // className="mb-3" // Removed individual margin
              >
                <KanbanCard
                  card={card}
                  columnId={column.id}
                  columnColor={columnColor}
                  variant={variant}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
        {column.cards.length === 0 && !isOver && (
          <p className="text-center text-xs text-slate-500 dark:text-slate-400 py-4 px-2">
            Drag cards here.
          </p>
        )}
      </div>
    </div>
  );
};
